import queryString from 'query-string'
import { throwError } from 'rxjs'
import { ajax } from 'rxjs/ajax'
import { catchError } from 'rxjs/operators'
import { env } from 'shared/env'

const baseURL = env.SERVER_ADDR

const objToQueryString = (obj) => {
  return Object.keys(obj)
    .map((key) => `${key}=${obj[key]}`)
    .join('&')
}

const requestHeader = (isFile = false) => {
  const headers = isFile ? {} : { 'Content-Type': 'application/json' }
  const userData = JSON.parse(localStorage.getItem('user_info') || '{}')
  let token: string | boolean = false
  if (userData.token) {
    token = userData.token
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

interface ErrorResponse {
  status: number
  message: string
}

const handleError = (operation: string) => (err: ErrorResponse) => {
  const errMsg = `Error in ${operation}()`

  if (err.status === 401) {
    localStorage.clear()
    window.location.href = '/auth/login'
  }
  return throwError(() => console.error(errMsg))
}

export const getCourierSummary = (filtersList) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/couriers/reports/summary?${objToQueryString(
      filtersList,
    )}`,
  }).pipe(catchError(handleError('getCourierSummary')))

export const getCourierBookings = (filters) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/booking?courier_id=${
      filters.courier_id || null
    }&to_be_delivered=${filters.to_be_delivered}&search=${
      filters.search
    }&page=${filters.page}&page_size=${filters.page_size}`,
  }).pipe(catchError(handleError('getCourierBookings')))

export const getGeneralCourierList = () =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/courier`,
  }).pipe(catchError(handleError('getGeneralCourierList')))

export const getCourierPayments = (payload) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/payments?${queryString.stringify(payload)}`,
  }).pipe(catchError(handleError('getCourierPayments')))
export const getCourierPartnerPayments = () =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/settings/billing/partner-invoices`,
  }).pipe(catchError(handleError('getCourierPayments')))

export const getReceivedPayments = (payment_id) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/payments/recorded?courier_payment_id=${payment_id}`,
  }).pipe(catchError(handleError('getReceivedPayments')))

export const createReceivedPayment = (payload) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/payments/record`,
    body: payload,
  }).pipe(catchError(handleError('createReceivedPayment')))

export const getCourierBalances = (courier_id = null) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/couriers/reports/balances?${
      courier_id ? 'courier_id=' + courier_id : ''
    }`,
  }).pipe(catchError(handleError('getCourierBalances')))

export const getCourierReportsByYear = (year) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/couriers/reports/yearly/${year}`,
  }).pipe(catchError(handleError('getCourierReportsByYear')))

export const accountingCategories = (data) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/accounting/coa/classification?${objToQueryString(
      data,
    )}`,
  }).pipe(catchError(handleError('accountingCategories')))

export const getReconcileSummary = (filtersList) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/reconcile/summary?${objToQueryString(
      filtersList,
    )}`,
  }).pipe(catchError(handleError('getReconcileSummary')))

export const getDiscrepantOrders = (filtersList) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/${
      filtersList.courier_id
    }/discrepant?${objToQueryString(filtersList)}`,
  }).pipe(catchError(handleError('getDiscrepantOrders')))

export const getDiscrepantDetails = (order_id, filters) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/discrepant/${order_id}?${objToQueryString(
      filters,
    )}`,
  }).pipe(catchError(handleError('getDiscrepantDetails')))

export const resolveDiscrepancy = (order_id, data) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/discrepant/${order_id}`,
    body: data,
  }).pipe(catchError(handleError('resolveDiscrepancy')))

export const createCourierComplaint = (order_id, data) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/complaint/${order_id}`,
    body: data,
  }).pipe(catchError(handleError('createCourierComplaint')))

export const updateBookingPayment = (order_id, data) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/booking/${order_id}/payment`,
    body: data,
  }).pipe(catchError(handleError('updateBookingPayment')))

export const getMonthlyPayments = (filters) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/couriers/reports/monthly?${objToQueryString(
      filters,
    )}`,
  }).pipe(catchError(handleError('getMonthlyPayments')))

export const uploadPaymentSheet = (file, data, force = false) => {
  const formData = new FormData()
  if (force) data['force'] = true
  if (file?.name) {
    formData.append('file', file, file.name)
  }
  formData.append('file_data', JSON.stringify(data))

  return ajax({
    headers: requestHeader(true),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/payments/upload`,
    body: formData,
  }).pipe(catchError(handleError('uploadPaymentSheet')))
}

export const InvoicePaymentsDetails = (id) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/courier/payments/${id}`,
  }).pipe(catchError(handleError('InvoicePaymentsDetails')))

export const getCourierDeliveryDetails = (filtersList) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/couriers/reports/delivery?${objToQueryString(
      filtersList,
    )}`,
  }).pipe(catchError(handleError('getCourierDeliveryDetails')))

export const getDarazStatements = () =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/payments/daraz-statements`,
  }).pipe(catchError(handleError('getAllPickupAddresses')))

export const getDarazStatementDetails = (data) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/payment/daraz-statements/details`,
    body: data,
  }).pipe(catchError(handleError('getDarazStatementDetails')))

export const getMonthWiseReconciliation = () =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/couriers/reports/monthwise-reconciliations`,
  }).pipe(catchError(handleError('getMonthWiseReconciliation')))

export const getMonthWiseReconciliationRecord = (filters) => {
  if (filters.courier_id === 'All') delete filters.courier_id
  return ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/couriers/reports/monthwise-reconciliation?${objToQueryString(
      filters,
    )}`,
  }).pipe(catchError(handleError('getMonthWiseReconciliationRecord')))
}

export const getMonthWiseReconciliationDetail = (filters: IFilters) => {
  if (filters.reconcile_status === 'All') delete filters.reconcile_status
  if (filters.courier_id === 'All') delete filters.courier_id
  const stringified = queryString.stringify(filters)

  return ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/couriers/reports/monthwise-reconciliation/details?${stringified}`,
  }).pipe(catchError(handleError('getMonthWiseReconciliationDetail')))
}

export const exportReconciliationReport = (filters) => {
  const { token } = JSON.parse(
    localStorage.getItem('user_info') || '{token: null}',
  )
  const downloadLink = document.createElement('a')
  downloadLink.href = `${baseURL}api/v2/export/reports/monthwise-reconciliation/${token}?${objToQueryString(
    filters,
  )}`
  document.body.appendChild(downloadLink)
  downloadLink.click()
  document.body.removeChild(downloadLink)
}
